import { adminPlaybookBySlugOptions } from '@doneverse/data/hooks'
import type { SchemaField } from '@doneverse/data/utils'
import { flattenSchema } from '@doneverse/data/utils'
import type { Playbook, PlaybookSchema } from '@doneverse/shared'
import type { UseMutationResult, UseQueryOptions } from '@tanstack/react-query'
import { useQuery } from '@tanstack/react-query'
import { useParams, Link } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { useMemo } from 'react'
import cmsStyles from '@/components/admin/cms/cms-shell.module.css'
import { PlaybookEditorTabs } from '@/components/admin/cms/PlaybookEditorShell'
import { PublishStatusBadge } from '@/components/admin/cms/PublishStatusBadge'
import { SchemaEditor, schemaEditorStyles as styles } from '@/components/admin/SchemaEditor'
import { PageLayout } from '@/components/layout/PageLayout'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'

/* ─── Config ───────────────────────────────────────────── */

export interface SchemaPageConfig {
  titlePrefix: string
  subtitle: string
  /** Return query options for loading the schema, or null to use playbook.schema directly */
  schemaQuery: ((slug: string) => UseQueryOptions<PlaybookSchema>) | null
  /** Hook that returns the save mutation — called inside the editor wrapper */
  useSaveMutation: (slug: string) => UseMutationResult<unknown, Error, { id: string; fields: SchemaField[] }>
}

/* ─── Inner Editor ─────────────────────────────────────── */

function SchemaEditorWrapper({ playbook, config }: { playbook: Playbook; config: SchemaPageConfig }) {
  const schemaQueryOpts = config.schemaQuery?.(playbook.slug)
  const disabledSchemaQuery = useMemo<UseQueryOptions<PlaybookSchema>>(
    () => ({
      queryKey: ['adminSchema', 'disabled', playbook.id],
      queryFn: async () => [],
      enabled: false,
    }),
    [playbook.id],
  )
  const { data: asyncSchema, isLoading } = useQuery({
    ...(schemaQueryOpts ?? disabledSchemaQuery),
  })

  const rawSchema = schemaQueryOpts ? asyncSchema : playbook.schema

  const initialFields = useMemo(
    () => (rawSchema && rawSchema.length > 0 ? flattenSchema(rawSchema) : []),
    [rawSchema],
  )

  const saveMutation = config.useSaveMutation(playbook.slug)

  if (schemaQueryOpts && isLoading) return <AdminSchemaSkeleton />

  return (
    <SchemaEditor
      key={playbook.id}
      initialFields={initialFields}
      onSave={(fields) => saveMutation.mutate({ id: playbook.id, fields })}
      saving={saveMutation.isPending}
      cancelElement={
        <Button type="button" variant="outline" asChild>
          <Link to="/admin/playbooks/$slug" params={{ slug: playbook.slug }} preload="intent">
            Cancel
          </Link>
        </Button>
      }
    />
  )
}

/* ─── Skeleton ─────────────────────────────────────────── */

export function AdminSchemaSkeleton() {
  return (
    <PageLayout title="Schema" subtitle="Loading..." fullHeight contentClassName="flex flex-col !p-0">
      <div className="flex flex-col flex-1 min-h-0">
        <div className={styles.pageContent}>
          <div className={styles.fieldsHeader}>
            <Skeleton className="h-4 w-24" />
            <div className="flex gap-2">
              <Skeleton className="h-8 w-20 rounded-md" />
              <Skeleton className="h-8 w-20 rounded-md" />
            </div>
          </div>
          <div className={styles.fieldsList}>
            <div className={`${styles.fieldCard} ${styles.fieldCardSection}`}>
              <div className={styles.fieldHeader}>
                <Skeleton className="h-4 w-6 rounded" />
                <Skeleton className="h-5 w-16 rounded" />
                <Skeleton className="h-4 w-40 rounded" />
              </div>
            </div>
            {[48, 36, 56].map((w) => (
              <div key={w} className={`${styles.fieldCard} ${styles.fieldCardQuestion}`}>
                <div className={styles.fieldHeader}>
                  <Skeleton className="h-4 w-6 rounded" />
                  <Skeleton className="h-5 w-20 rounded" />
                  <Skeleton className={`h-4 w-${w} rounded`} />
                </div>
              </div>
            ))}
            <div className={`${styles.fieldCard} ${styles.fieldCardSection}`}>
              <div className={styles.fieldHeader}>
                <Skeleton className="h-4 w-6 rounded" />
                <Skeleton className="h-5 w-16 rounded" />
                <Skeleton className="h-4 w-32 rounded" />
              </div>
            </div>
            {[44, 52].map((w) => (
              <div key={w} className={`${styles.fieldCard} ${styles.fieldCardQuestion}`}>
                <div className={styles.fieldHeader}>
                  <Skeleton className="h-4 w-6 rounded" />
                  <Skeleton className="h-5 w-20 rounded" />
                  <Skeleton className={`h-4 w-${w} rounded`} />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className={styles.pageFooter}>
          <Skeleton className="h-9 w-20 rounded-md" />
          <Skeleton className="h-9 w-28 rounded-md" />
        </div>
      </div>
    </PageLayout>
  )
}

/* ─── Page ─────────────────────────────────────────────── */

export function AdminSchemaPage({ config }: { config: SchemaPageConfig }) {
  const { slug } = useParams({ strict: false })

  const { data: playbook, isLoading, isError } = useQuery(adminPlaybookBySlugOptions(slug!))

  if (isLoading) return <AdminSchemaSkeleton />

  if (isError || !playbook) {
    return (
      <PageLayout title={config.titlePrefix} subtitle="Error">
        <div className="text-center py-12">
          <p className="text-muted-foreground mb-4">Playbook not found.</p>
          <Button variant="outline" asChild>
            <Link to="/admin/playbooks" preload="intent">
              <ArrowLeft className="w-4 h-4 mr-2" />
              All playbooks
            </Link>
          </Button>
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout
      // Titled by the playbook, not the tab: this is one screen among the
      // editor's tabs now, not a standalone page you arrive at from a list.
      title={
        <span className={cmsStyles.title}>
          {playbook.name}
          <PublishStatusBadge status={playbook.status} />
        </span>
      }
      subtitle={config.subtitle}
      actions={
        <Button variant="outline" asChild>
          <Link to="/admin/playbooks" preload="intent">
            <ArrowLeft className="w-4 h-4 mr-2" />
            All playbooks
          </Link>
        </Button>
      }
      fullHeight
      contentClassName="flex flex-col !p-0"
    >
      <PlaybookEditorTabs slug={playbook.slug} type={playbook.type} />
      <SchemaEditorWrapper key={playbook.id} playbook={playbook} config={config} />
    </PageLayout>
  )
}
